// A text field whose answer may be a prediction (shown grey).
//
// Kevin, 2026-10-17: "I clicked here and I should not have to back space", then "if I click on
// that and click out then the prediction should come back". So clicking into a predicted field
// shows it EMPTY, with the prediction as grey placeholder text; whatever is typed or pasted is
// the new value; leaving without typing shows the prediction again. Focusing changes nothing
// that is stored -- the prediction stays the answer until something is typed -- so clicking
// around never re-runs the operations. A field that is not predicted is an ordinary input.
// ComboBox does the same for the type-or-pick fields.

import { useState, type InputHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { ghostView } from "../util/prediction";

type Own = { predicted: boolean; value: string };

function useGhost(predicted: boolean) {
  const [focusedEmpty, setFocusedEmpty] = useState(false);
  return {
    ghosting: predicted && focusedEmpty,
    start: () => predicted && setFocusedEmpty(true),
    end: () => setFocusedEmpty(false),
  };
}

export function PredictedInput({
  predicted, value, placeholder, onFocus, onChange, onBlur, ...rest
}: Own & Omit<InputHTMLAttributes<HTMLInputElement>, "value">) {
  const g = useGhost(predicted);
  return (
    <input
      {...rest}
      {...ghostView(g.ghosting, value, placeholder)}
      onFocus={(e) => { g.start(); onFocus?.(e); }}
      onChange={(e) => { g.end(); onChange?.(e); }}
      // Leaving an untouched predicted field is not an edit: its blur handler (which commits
      // what the box holds) would commit the empty box.
      onBlur={(e) => { const untouched = g.ghosting; g.end(); if (!untouched) onBlur?.(e); }}
    />
  );
}

export function PredictedTextarea({
  predicted, value, placeholder, onFocus, onChange, onBlur, ...rest
}: Own & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value">) {
  const g = useGhost(predicted);
  return (
    <textarea
      {...rest}
      {...ghostView(g.ghosting, value, placeholder)}
      onFocus={(e) => { g.start(); onFocus?.(e); }}
      onChange={(e) => { g.end(); onChange?.(e); }}
      onBlur={(e) => { const untouched = g.ghosting; g.end(); if (!untouched) onBlur?.(e); }}
    />
  );
}
